import {
  type FC,
  isValidElement,
  type MouseEvent,
  type ReactNode,
  type Ref,
} from 'react'
import { KibButtonNew } from '@chewy/kib-controls-react'
import { KibArrowRightIcon } from '@chewy/kib-icons-react'
import classNames from 'classnames'

import RhapHoverTooltip from '~/components/common/RhapHoverTooltip'

import '@chewy/kib-controls-styles/dist/styles/kib-button-new.css'
import styles from './AgentChatLinkButton.module.scss'

/**
 * Extract text from React children for aria-label
 */
export const getChildrenText = (node: ReactNode): string => {
  if (typeof node === 'string') {
    return node
  }
  if (typeof node === 'number') {
    return String(node)
  }
  if (Array.isArray(node)) {
    return node.map(getChildrenText).join('')
  }
  if (isValidElement(node) && node.props.children) {
    return getChildrenText(node.props.children)
  }
  return ''
}

export interface AgentChatLinkButtonProps {
  /** Accessible label for the button; defaults to tooltipContent or children text if absent */
  ariaLabel?: string
  children?: ReactNode
  className?: string
  /** Click handler; callers typically preventDefault and run their own logic */
  onClick: (e: MouseEvent) => void
  /** Tooltip text shown on hover */
  tooltipContent: string
}

/**
 * Shared presentational button for agent chat link actions (e.g. SOAP link, lab test link).
 * Renders an icon-only tertiary button with tooltip; no business logic.
 */
export const AgentChatLinkButton: FC<AgentChatLinkButtonProps> = ({
  ariaLabel,
  children,
  className,
  onClick,
  tooltipContent,
}) => {
  const resolvedAriaLabel =
    ariaLabel || getChildrenText(children) || tooltipContent

  const buttonTrigger = (triggerProps: {
    'aria-describedby': string
    onBlur: () => void
    onFocus: () => void
    onMouseEnter: () => void
    onMouseLeave: () => void
    ref: Ref<HTMLElement>
  }) => (
    <span {...triggerProps} style={{ display: 'inline-block' }}>
      <KibButtonNew
        iconOnly
        aria-label={resolvedAriaLabel}
        className={classNames(styles.button, className)}
        emphasis="tertiary"
        icon={<KibArrowRightIcon />}
        size="small"
        theme="utility"
        onClick={onClick}
      />
    </span>
  )

  return (
    <RhapHoverTooltip
      content={tooltipContent}
      trigger={buttonTrigger}
      variant="dark"
    />
  )
}
